<script setup lang="ts">
import type { TooltipContentEmits, TooltipContentProps } from 'reka-ui';
import { computed, inject, type HTMLAttributes, type Ref } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { TooltipContent as TooltipContentPrimitive, TooltipPortal, useForwardPropsEmits } from 'reka-ui';

import { cn } from '@/lib/utils';

defineOptions({
    name: 'UiTooltipContent',
    inheritAttrs: false,
});

const props = withDefaults(defineProps<TooltipContentProps & { class?: HTMLAttributes['class'] }>(), {
    class: undefined,
    side: 'top',
    sideOffset: 8,
});
const emits = defineEmits<TooltipContentEmits>();

const delegatedProps = reactiveOmit(props, 'class');
const forwarded = useForwardPropsEmits(delegatedProps, emits);

const portalContainerRef = inject<Ref<HTMLElement | null> | undefined>('portalContainerRef', undefined);
const collisionBoundary = computed<Element[]>(() => {
    const dialogBoundary = portalContainerRef?.value?.closest('dialog');
    return dialogBoundary ? [dialogBoundary] : [];
});
</script>

<template>
    <TooltipPortal :to="portalContainerRef ? portalContainerRef : 'body'">
        <TooltipContentPrimitive
            v-bind="{ ...forwarded, ...$attrs }"
            :collision-boundary="collisionBoundary"
            :class="
                cn(
                    'data-[state=closed]:animate-out data-[state=instant-open]:animate-in data-[state=delayed-open]:animate-in data-[state=closed]:fade-out-0 data-[state=instant-open]:fade-in-0 data-[state=delayed-open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=instant-open]:zoom-in-95 data-[state=delayed-open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 font-default z-70 w-fit max-w-[240px] rounded-[4px] bg-[var(--color-dark-400)] px-2 py-2 text-center text-[12px] leading-[15.6px] font-normal whitespace-nowrap text-white shadow-[0_2px_6px_rgba(0,0,0,0.16)] outline-none',
                    props.class,
                )
            "
        >
            <slot />
        </TooltipContentPrimitive>
    </TooltipPortal>
</template>
